import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Chip,
  IconButton
} from '@mui/material';
import {
  Download,
  Upload,
  Description,
  History,
  LocationOn,
  Flight,
  Build,
  Visibility,
  ExpandMore
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ROLES } from '../constants/roles';

// Helper functions
export const getStatusColor = (status) => {
  switch (status) {
    case 'AVAILABLE': return 'success';
    case 'IN_FLIGHT': return 'info';
    case 'MAINTENANCE': return 'warning';
    case 'INSPECTION': return 'warning';
    case 'GROUNDED': return 'error';
    default: return 'default';
  }
};

export const getDocumentTypeLabel = (type) => {
  const labels = {
    'MANUAL': 'Manual',
    'SPECIFICATIONS': 'Specifications',
    'WARRANTY': 'Warranty',
    'SERVICE_HISTORY': 'Service History',
    'CERTIFICATE_OF_REGISTRATION': 'Certificate of Registration',
    'CERTIFICATE_OF_AIRWORTHINESS': 'Certificate of Airworthiness',
    'INSURANCE': 'Insurance',
    'TYPE_CERTIFICATE': 'Type Certificate',
    'OPERATOR_PERMIT': 'Operator Permit',
    'IMPORT_DOCUMENTS': 'Import Documents',
    'MODIFICATIONS': 'Modifications',
    'OTHER': 'Other'
  };
  return labels[type] || type;
};

export const groupDocumentsByType = (documents) => {
  const grouped = {};
  documents?.forEach(doc => {
    if (!grouped[doc.documentType]) {
      grouped[doc.documentType] = [];
    }
    grouped[doc.documentType].push(doc);
  });
  return grouped;
};

const AircraftDetails = ({
  aircraft,
  history,
  documents,
  detailTab,
  setDetailTab,
  onDocumentDownload,
  onDocumentUpload,
  canUploadDocuments,
  canViewDetails
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const groupedDocs = groupDocumentsByType(documents);

  // Ensure detailTab is within valid range based on canViewDetails
  useEffect(() => {
    if (!canViewDetails && detailTab > 1) {
      setDetailTab(0);
    }
  }, [canViewDetails, detailTab, setDetailTab]);

  const handleViewSectorDetails = (flight) => {
    const sectorId = flight.sectorId?._id?.toString() || flight.sectorId?.toString() || flight.sector?._id?.toString();
    const tripId = flight.sector?.trip?._id?.toString() || flight.sector?.trip?._id;
    const charterNumber = flight.sector?.trip?.charterNumber;
    const sectorSeqNo = flight.sector?.seqNo;

    if (!sectorId) {
      alert('Sector information not available');
      return;
    }

    if ([ROLES.SUPER_ADMIN, ROLES.OPERATIONS_MANAGER, ROLES.OPERATIONS_EXECUTIVE, ROLES.ACCOUNTABLE_MANAGER].includes(user?.role)) {
      navigate('/ops', {
        state: {
          selectedTripId: tripId,
          selectedSectorId: sectorId,
          charterNumber: charterNumber,
          sectorSeqNo: sectorSeqNo
        }
      });
    } else if (user?.role === ROLES.SALES) {
      navigate('/sales', {
        state: {
          selectedTripId: tripId,
          selectedSectorId: sectorId,
          charterNumber: charterNumber,
          sectorSeqNo: sectorSeqNo
        }
      });
    } else if ([ROLES.CA_MANAGER, ROLES.CA_TECHNICAL_OFFICER, ROLES.PLANNING_MANAGER, ROLES.CAMO_QM_MANAGER, ROLES.CAMO_QM_OFFICER].includes(user?.role)) {
      navigate('/camo-dashboard');
    } else if (user?.role === ROLES.CREW) {
      navigate('/crew', {
        state: {
          selectedSectorId: sectorId,
          tripId: tripId,
          charterNumber: charterNumber
        }
      });
    } else {
      navigate('/ops', {
        state: {
          selectedTripId: tripId,
          selectedSectorId: sectorId,
          charterNumber: charterNumber,
          sectorSeqNo: sectorSeqNo
        }
      });
    }
  };

  const handleViewMaintenanceDetails = (maintenance) => {
    navigate('/maintenance', {
      state: {
        selectedWorkOrderId: maintenance._id
      }
    });
  };

  return (
    <Box sx={{ p: 3, bgcolor: 'background.default' }}>
      <Tabs value={detailTab} onChange={(e, v) => setDetailTab(v)}>
        <Tab icon={<Description />} label="Documents" />
        <Tab icon={<History />} label="Activity History" />
        {canViewDetails && <Tab icon={<LocationOn />} label="Details" />}
        {canViewDetails && <Tab icon={<Flight />} label="Flight History" />}
        {canViewDetails && <Tab icon={<Build />} label="Maintenance History" />}
      </Tabs>

      {detailTab === 0 && (
        <Box sx={{ mt: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Document Library</Typography>
            {canUploadDocuments && (
              <Button
                variant="outlined"
                startIcon={<Upload />}
                onClick={() => onDocumentUpload(aircraft)}
              >
                Upload Document
              </Button>
            )}
          </Box>
          {documents.length === 0 ? (
            <Alert severity="info">No documents uploaded yet.</Alert>
          ) : (
            Object.keys(groupedDocs).map(docType => (
              <Accordion key={docType} sx={{ mt: 1 }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle1">
                    {getDocumentTypeLabel(docType)} ({groupedDocs[docType].length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List>
                    {groupedDocs[docType].map((doc, idx) => (
                      <React.Fragment key={doc._id}>
                        <ListItem>
                          <ListItemText
                            primary={`${doc.originalFileName} (v${doc.version})`}
                            secondary={
                              <>
                                Uploaded: {new Date(doc.uploadedAt).toLocaleString()}
                                {doc.uploadedBy && ` by ${doc.uploadedBy.firstName} ${doc.uploadedBy.lastName}`}
                                {doc.description && ` - ${doc.description}`}
                              </>
                            }
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              onClick={() => onDocumentDownload(aircraft._id, doc._id, doc.originalFileName)}
                            >
                              <Download />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                        {idx < groupedDocs[docType].length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            ))
          )}
        </Box>
      )}

      {detailTab === 1 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="h6" gutterBottom>Activity History</Typography>
          {!history?.auditLogs || history.auditLogs.length === 0 ? (
            <Alert severity="info">No history available.</Alert>
          ) : (
            <List>
              {history.auditLogs.map((log, idx) => (
                <React.Fragment key={log._id}>
                  <ListItem>
                    <ListItemText
                      primary={`${log.action} - ${new Date(log.createdAt).toLocaleString()}`}
                      secondary={
                        <>
                          {log.actorId && `By: ${log.actorId.firstName} ${log.actorId.lastName} (${log.actorId.role})`}
                          {log.meta?.changedFields && ` - Changed: ${log.meta.changedFields.join(', ')}`}
                        </>
                      }
                    />
                  </ListItem>
                  {idx < history.auditLogs.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>
      )}

      {detailTab === 2 && canViewDetails && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="h6" gutterBottom>Aircraft Details</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2">Registration</Typography>
              <Typography>{aircraft.registration}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2">Manufacturer & Model</Typography>
              <Typography>{aircraft.manufacturer} {aircraft.model}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2">Serial Number</Typography>
              <Typography>{aircraft.serialNumber}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2">Year of Manufacture</Typography>
              <Typography>{aircraft.yearOfManufacture || '—'}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2">Aircraft Type</Typography>
              <Typography>{aircraft.aircraftType || '—'}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2">Max Passengers</Typography>
              <Typography>{aircraft.maxPassengers || '—'}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2">Current Status</Typography>
              <Chip
                label={aircraft.status}
                color={getStatusColor(aircraft.status)}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2">Flight Hours</Typography>
              <Typography>{aircraft.flightHours || 0} hrs</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                Weight Specifications
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2">Basic Empty Weight</Typography>
              <Typography>{aircraft.basicEmptyWeight ? `${aircraft.basicEmptyWeight} ${aircraft.basicEmptyWeightUnit || 'kg'}` : '—'}</Typography>
            </Grid>
            {aircraft.maxAllUpWeight && (
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Max All-Up Weight</Typography>
                <Typography>{aircraft.maxAllUpWeight} {aircraft.maxAllUpWeightUnit || 'kg'}</Typography>
              </Grid>
            )}
            {aircraft.maxRampWeight && (
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Max Ramp Weight</Typography>
                <Typography>{aircraft.maxRampWeight} {aircraft.maxRampWeightUnit || 'kg'}</Typography>
              </Grid>
            )}
            {aircraft.maxLandingWeight && (
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Max Landing Weight</Typography>
                <Typography>{aircraft.maxLandingWeight} {aircraft.maxLandingWeightUnit || 'kg'}</Typography>
              </Grid>
            )}
            {aircraft.maxFuelWeight && (
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Max Fuel Weight</Typography>
                <Typography>{aircraft.maxFuelWeight} {aircraft.maxFuelWeightUnit || 'kg'}</Typography>
              </Grid>
            )}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                Fuel Specifications
              </Typography>
            </Grid>
            {aircraft.fuelCapacity && (
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Fuel Capacity</Typography>
                <Typography>{aircraft.fuelCapacity} L</Typography>
              </Grid>
            )}
            {aircraft.fuelConsumptionPerHour && (
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Fuel Consumption per Hour</Typography>
                <Typography>{aircraft.fuelConsumptionPerHour} {aircraft.fuelConsumptionPerHourUnit || 'L'}</Typography>
              </Grid>
            )}
            {aircraft.fuelConsumptionPerHourWeight && (
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Fuel Consumption per Hour (Weight)</Typography>
                <Typography>
                  {aircraft.fuelConsumptionPerHourWeight}{' '}
                  {aircraft.maxFuelWeightUnit || aircraft.basicEmptyWeightUnit || 'kg'}
                </Typography>
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2">Last Inspection</Typography>
              <Typography>
                {aircraft.lastInspection ? new Date(aircraft.lastInspection).toLocaleDateString() : 'Not scheduled'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2">Next Inspection</Typography>
              <Typography>
                {aircraft.nextInspection ? new Date(aircraft.nextInspection).toLocaleDateString() : 'Not scheduled'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2">Current Location</Typography>
              <Typography>
                {aircraft.currentLocation?.airport || '—'}
                {aircraft.currentLocation?.hangar && ` / ${aircraft.currentLocation.hangar}`}
              </Typography>
            </Grid>
            {aircraft.remarks && (
              <Grid item xs={12}>
                <Typography variant="subtitle2">Remarks</Typography>
                <Typography>{aircraft.remarks}</Typography>
              </Grid>
            )}
          </Grid>
        </Box>
      )}

      {detailTab === 3 && canViewDetails && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="h6" gutterBottom>Flight History</Typography>
          {!history?.flightHistory || history.flightHistory.length === 0 ? (
            <Alert severity="info">No flight history available.</Alert>
          ) : (
            <List>
              {history.flightHistory.map((flight, idx) => (
                <React.Fragment key={flight._id}>
                  <ListItem>
                    <ListItemText
                      primary={
                        <>
                          {flight.sector?.trip?.charterNumber || 'N/A'} - 
                          Sector {flight.sector?.seqNo || 'N/A'} • 
                          {flight.sector?.departure?.airport || 'N/A'} → {flight.sector?.arrival?.airport || 'N/A'}
                          {flight.entryType === 'POST_FLIGHT' && flight.flightTime && ` • ${flight.flightTime} hrs`}
                        </>
                      }
                      secondary={
                        <>
                          {new Date(flight.createdAt).toLocaleString()} • 
                          {flight.entryType === 'POST_FLIGHT' ? 'Post-Flight' : 'Pre-Flight'} Entry
                          {flight.createdBy && ` by ${flight.createdBy.firstName} ${flight.createdBy.lastName}`}
                          {flight.takeoffTime && ` • Takeoff: ${new Date(flight.takeoffTime).toLocaleString()}`}
                          {flight.landingTime && ` • Landing: ${new Date(flight.landingTime).toLocaleString()}`}
                          {flight.fuelConsumed && ` • Fuel: ${flight.fuelConsumed}L`}
                          {flight.remarks && ` • ${flight.remarks}`}
                        </>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Visibility />}
                        onClick={() => handleViewSectorDetails(flight)}
                        disabled={!flight.sectorId && !flight.sector?._id}
                      >
                        View Sector
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                  {idx < history.flightHistory.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>
      )}

      {detailTab === 4 && canViewDetails && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="h6" gutterBottom>Maintenance History</Typography>
          {!history?.maintenanceHistory || history.maintenanceHistory.length === 0 ? (
            <Alert severity="info">No maintenance history available.</Alert>
          ) : (
            <List>
              {history.maintenanceHistory.map((maintenance, idx) => (
                <React.Fragment key={maintenance._id}>
                  <ListItem>
                    <ListItemText
                      primary={
                        <>
                          {maintenance.title} • 
                          <Chip
                            label={maintenance.status}
                            color={maintenance.status === 'COMPLETED' ? 'success' : maintenance.status === 'CANCELLED' ? 'error' : 'default'}
                            size="small"
                            sx={{ ml: 1 }}
                          />
                        </>
                      }
                      secondary={
                        <>
                          WO #{maintenance.workOrderNumber || maintenance.woNumber || (maintenance._id && maintenance._id.toString().slice(-6))} • 
                          {maintenance.type} • 
                          Created: {new Date(maintenance.createdAt).toLocaleString()}
                          {maintenance.createdBy && ` by ${maintenance.createdBy.firstName} ${maintenance.createdBy.lastName}`}
                          {maintenance.completedDate && ` • Completed: ${new Date(maintenance.completedDate).toLocaleDateString()}`}
                          {maintenance.description && ` • ${maintenance.description}`}
                        </>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Visibility />}
                        onClick={() => handleViewMaintenanceDetails(maintenance)}
                      >
                        View Details
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                  {idx < history.maintenanceHistory.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>
      )}
    </Box>
  );
};

export default AircraftDetails;
